import PropTypes from "prop-types";
import { useState } from "react";
import { API } from "../../../services/api";
import { FieldLabel } from "../AdminCommon";
import { INVALID_INVITATION_EMAIL, formatInvitationExpiry, isValidInvitationEmail, transferStatus, withoutTrailingDots } from "./ehpadInvitations";
import {
    btnBlackStyle,
    btnOutlineStyle,
    errorStyle,
    fieldStyle,
    inputStyle,
    modalBackdropStyle,
    modalCloseButtonStyle,
    modalHeaderStyle,
    modalStyle,
} from "../styles/adminDashboardStyles";
import ModalBackdrop from "../../../components/ModalBackdrop";

export function TransferEhpadPanel({ transfers, clock, onInvited, onChanged }) {
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    const failureReason = err => withoutTrailingDots((err.response?.data?.message ?? "erreur inconnue").trim());

    const handleSend = async (event) => {
        event.preventDefault();
        setError(null);
        const value = email.trim();
        if (!isValidInvitationEmail(value)) {
            setError(INVALID_INVITATION_EMAIL);
            return;
        }
        setSending(true);
        try {
            const { data } = await API.post("/admin/ehpads/invitations/transfers", { email: value });
            setEmail("");
            onInvited(data);
            onChanged();
        } catch (err) {
            setError(`Le transfert n'a pas pu être demandé : ${failureReason(err)}.`);
        } finally {
            setSending(false);
        }
    };

    const handleCancel = async (transferId) => {
        setError(null);
        try {
            await API.delete(`/admin/ehpads/invitations/transfers/${transferId}`);
            onChanged();
        } catch (err) {
            setError(`Le transfert n'a pas pu être annulé : ${failureReason(err)}.`);
        }
    };

    return (
        <div>
            <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#6b7280", lineHeight: 1.5 }}>
                Pour un EHPAD déjà inscrit sur le portail avec une autre pharmacie, dont le raccordement a été annulé :
                son responsable reçoit un lien et un code. À la validation, l'EHPAD rejoint votre pharmacie ;
                son historique reste à l'ancienne pharmacie.
            </p>
            <form onSubmit={handleSend} noValidate style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ ...fieldStyle, flex: "1 1 280px", maxWidth: "420px" }}>
                    <FieldLabel htmlFor="admin-ehpad-transfer-email">Email du responsable de l'EHPAD</FieldLabel>
                    <input
                        id="admin-ehpad-transfer-email"
                        type="text"
                        inputMode="email"
                        autoComplete="off"
                        style={inputStyle}
                        placeholder="direction@ehpad-les-acacias.fr"
                        value={email}
                        onChange={event => setEmail(event.target.value)}
                    />
                </div>
                <button type="submit" disabled={sending} style={btnBlackStyle}>
                    {sending ? "Envoi..." : "Envoyer l'invitation de transfert"}
                </button>
            </form>
            {error && <p style={errorStyle}>{error}</p>}
            {transfers.length > 0 && (
                <div style={{ display: "grid", gap: "8px", marginTop: "14px" }}>
                    {transfers.map(transfer => (
                        <div
                            key={transfer.id}
                            data-testid={`transfer-${transfer.id}`}
                            style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: "8px" }}
                        >
                            <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: "#111827" }}>{transfer.ehpadName}</div>
                                <div style={{ fontSize: "12px", color: "#6b7280" }}>{transfer.email}</div>
                                <div style={{ marginTop: "4px", fontSize: "11px", fontWeight: 600, color: transfer.blocked ? "#dc2626" : "#92400e" }}>
                                    {transferStatus(transfer, clock)}
                                </div>
                            </div>
                            <button type="button" onClick={() => handleCancel(transfer.id)} style={btnOutlineStyle}>
                                Annuler le transfert
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
TransferEhpadPanel.propTypes = {
    transfers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        ehpadName: PropTypes.string,
        email: PropTypes.string,
        failedAttempts: PropTypes.number,
        maxFailedAttempts: PropTypes.number,
        blocked: PropTypes.bool,
        expiresAt: PropTypes.string,
    })).isRequired,
    clock: PropTypes.number.isRequired,
    onInvited: PropTypes.func.isRequired,
    onChanged: PropTypes.func.isRequired,
};

export function InvitationCodeModal({ invitation, onClose }) {
    const expiry = formatInvitationExpiry(invitation.expiresAt);
    const code = invitation.verificationCode ?? "";
    return (
        <div style={modalBackdropStyle}>
            <ModalBackdrop onClose={onClose} />
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Code de raccordement"
                style={{ ...modalStyle, position: "relative", zIndex: 1 }}
            >
                <div style={modalHeaderStyle}>
                    <h2 style={{ margin: 0, fontSize: "18px" }}>Code de raccordement</h2>
                    <button type="button" onClick={onClose} aria-label="Fermer" style={modalCloseButtonStyle}>×</button>
                </div>
                <div style={{ display: "grid", gap: "16px" }}>
                    <p style={{ margin: 0, color: "#374151", lineHeight: 1.6 }}>
                        Invitation envoyée à <strong>{invitation.email}</strong>. Communiquez ce code à l'EHPAD :
                        il devra le saisir pour valider le raccordement. Il ne figure pas dans le mail.
                    </p>
                    <div
                        aria-label="Code à 6 chiffres"
                        style={{ textAlign: "center", fontFamily: "'Courier New', Courier, monospace", fontSize: "34px", fontWeight: 700, letterSpacing: "10px", color: "#0C3B3C", background: "#F4F1EA", borderLeft: "4px solid #17B978", borderRadius: "8px", padding: "16px 0" }}
                    >
                        {code}
                    </div>
                    <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
                        {expiry ? `Valable jusqu'au ${expiry}. ` : ""}Il ne sera plus affiché après la fermeture de cette fenêtre.
                    </p>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button type="button" onClick={onClose} style={btnBlackStyle}>J'ai noté le code</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Phrase d'échec d'envoi d'une invitation, à partir du message du serveur (sans double point final). */
InvitationCodeModal.propTypes = {
    invitation: PropTypes.shape({
        email: PropTypes.string,
        expiresAt: PropTypes.string,
        verificationCode: PropTypes.string,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
};

export function InvitationEmailField({ inputId, value, onChange, submitLabel, children }) {
    return (
        <div style={fieldStyle}>
            <FieldLabel htmlFor={inputId}>Email pour le raccordement (facultatif)</FieldLabel>
            <input
                id={inputId}
                type="text"
                inputMode="email"
                autoComplete="off"
                style={{ ...inputStyle, maxWidth: "420px" }}
                placeholder="direction@ehpad-les-acacias.fr"
                value={value}
                onChange={e => onChange(e.target.value)}
            />
            <span style={{ fontSize: "12px", color: "#6b7280", marginTop: "6px" }}>
                Si renseigné, l'EHPAD reçoit une invitation de raccordement (lien et code valables 30 minutes) après un clic sur « {submitLabel} ».
            </span>
            {children}
        </div>
    );
}
InvitationEmailField.propTypes = {
    inputId: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    submitLabel: PropTypes.string.isRequired,
    children: PropTypes.node,
};
